import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  Alert,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  TagChip,
  TextField,
  Typography,
  statusTone,
  useRowActionsColumn,
} from '@munifocus/ui';
import {
  SERVICE_REQUEST_KINDS,
  SERVICE_REQUEST_NEXT,
  type ServiceRequestKind,
  type ServiceRequestStatus,
  type ServiceRequestSummary,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { useScope } from '../providers/ScopeProvider';
import { useContracts } from '../lib/administration-api';
import { formatDate } from '../lib/relative-time';
import { useFiles } from '../lib/files-api';
import { useMutationToast } from '../lib/mutation-toast';
import { useSubmitValidation } from '../lib/useSubmitValidation';
import {
  useCreateServiceRequest,
  useServiceRequests,
  useTransitionServiceRequest,
  type RequestSide,
} from '../lib/service-requests-api';

/**
 * Requests between a municipality and its contractor (client-meeting D5).
 *
 * One screen for both sides: in a municipality's scope it lists what the
 * municipality asked its contractor and what the contractor asked it; in a
 * company's scope, the same from the other end. Whoever received a request
 * moves it on, through to settled or declined with a written answer.
 */
export function Requests() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const actionsColumn = useRowActionsColumn();
  const { active, can } = useScope();
  const side: RequestSide | null =
    active?.kind === 'local_government' || active?.kind === 'company'
      ? { kind: active.kind, id: active.id }
      : null;

  const requests = useServiceRequests(side);
  const create = useCreateServiceRequest(side);
  const move = useTransitionServiceRequest();
  // Who the caller can ask: the other party on each contract not terminated.
  const contracts = useContracts(
    side?.kind === 'company' ? { companyId: side.id } : { localGovernmentId: side?.id },
  );
  const counterparts = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of contracts.data ?? []) {
      if (c.status === 'terminated') continue;
      if (side?.kind === 'company') seen.set(c.localGovernmentId, c.localGovernmentName);
      else seen.set(c.companyId, c.companyName);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [contracts.data, side?.kind]);

  const [asking, setAsking] = useState(false);
  const [counterpartId, setCounterpartId] = useState('');
  const [kind, setKind] = useState<ServiceRequestKind>('enforcement');
  const [subject, setSubject] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [open, setOpen] = useState<ServiceRequestSummary | null>(null);
  const [answer, setAnswer] = useState('');
  const [linkFileId, setLinkFileId] = useState('');
  // The files the handling side can link: its own, at the request's town.
  const files = useFiles();
  const linkable = useMemo(
    () =>
      (files.data?.items ?? []).filter(
        (f) => open && f.localGovernmentId === open.localGovernmentId,
      ),
    [files.data, open],
  );

  const received = (r: ServiceRequestSummary) =>
    side?.kind === 'company' ? r.direction === 'lg_to_company' : r.direction === 'company_to_lg';
  const counterpartName = (r: ServiceRequestSummary) =>
    side?.kind === 'company' ? r.localGovernmentName : r.companyName;

  const v = useSubmitValidation({
    counterpartId: counterpartId ? null : t('common.validation.choose'),
    subject: subject.trim().length >= 3 ? null : t('common.validation.minLength', { count: 3 }),
  });

  const closeAsk = () => {
    v.reset();
    setAsking(false);
    setSubject('');
    setAddress('');
    setDescription('');
  };

  const moveTo = async (to: ServiceRequestStatus) => {
    if (!open) return;
    try {
      const updated = await move.mutateAsync({
        id: open.id,
        to,
        summary: answer || undefined,
        linkedFileId: linkFileId || undefined,
      });
      toast.success(`gov.requests.moved.${to}`);
      setOpen(updated);
      setAnswer('');
      setLinkFileId('');
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
    }
  };

  return (
    <AppShell title={t('gov.requests.title')}>
      <PageIntro>
        {t(side?.kind === 'company' ? 'gov.requests.introCompany' : 'gov.requests.introLg')}
      </PageIntro>
      {!side ? (
        <Alert severity="info">{t('gov.requests.pickScope')}</Alert>
      ) : (
        <DataTable
          rows={requests.data ?? []}
          getRowKey={(r) => r.id}
          isLoading={requests.isLoading}
          emptyLabel={t('gov.requests.empty')}
          pageSize={25}
          initialSortKey="updated"
          initialSortDir="desc"
          onRowClick={(r) => setOpen(r)}
          search={{
            placeholder: t('gov.requests.search'),
            of: (r) => `${r.subject} ${r.address} ${counterpartName(r)}`,
          }}
          action={
            can('request:submit') && counterparts.length > 0 ? (
              <ActionButton
                label={t('gov.requests.new')}
                icon={Icons.Send}
                onClick={() => {
                  setCounterpartId(counterparts[0]?.[0] ?? '');
                  setAsking(true);
                }}
              />
            ) : null
          }
          columns={[
            {
              key: 'subject',
              label: t('gov.requests.subject'),
              primary: true,
              sortValue: (r) => r.subject,
              cell: (r) => (
                <Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.subject}
                  </Typography>
                  {r.address && (
                    <Typography variant="caption" color="text.secondary">
                      {r.address}
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              key: 'direction',
              label: t('gov.requests.direction'),
              sortValue: (r) => (received(r) ? 0 : 1),
              cell: (r) => (
                <TagChip
                  label={t(received(r) ? 'gov.requests.receivedFrom' : 'gov.requests.sentTo', {
                    name: counterpartName(r),
                  })}
                />
              ),
            },
            {
              key: 'kind',
              label: t('gov.requests.kind'),
              sortValue: (r) => r.kind,
              cell: (r) => t(`gov.requests.kinds.${r.kind}`),
            },
            {
              key: 'status',
              label: t('gov.requests.status'),
              sortValue: (r) => r.status,
              cell: (r) => (
                <StatusChip
                  status={statusTone.serviceRequest[r.status]}
                  label={t(`gov.requests.statuses.${r.status}`)}
                />
              ),
            },
            {
              key: 'updated',
              label: t('gov.requests.updated'),
              nowrap: true,
              sortValue: (r) => r.updatedAt,
              cell: (r) => formatDate(r.updatedAt, i18n.language),
            },
            {
              ...actionsColumn,
              cell: (r) => (
                <RowActions>
                  <RowAction
                    label={t('gov.requests.open')}
                    icon={Icons.ChevronRight}
                    onClick={() => setOpen(r)}
                  />
                </RowActions>
              ),
            },
          ]}
        />
      )}

      <Dialog open={asking} onClose={closeAsk} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.requests.new')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t(side?.kind === 'company' ? 'gov.requests.toLg' : 'gov.requests.toCompany')}
              {...v.field('counterpartId')}
              value={counterpartId}
              onChange={(e) => setCounterpartId(e.target.value)}
              fullWidth
            >
              {counterparts.map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('gov.requests.kind')}
              value={kind}
              onChange={(e) => setKind(e.target.value as ServiceRequestKind)}
              helperText={t(`gov.requests.kindHelp.${kind}`)}
              fullWidth
            >
              {SERVICE_REQUEST_KINDS.map((k) => (
                <MenuItem key={k} value={k}>
                  {t(`gov.requests.kinds.${k}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.requests.subject')}
              {...v.field('subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label={t('gov.requests.address')}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('gov.requests.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAsk}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={create.isPending}
            onClick={v.guard(async () => {
              try {
                await create.mutateAsync({
                  counterpartId,
                  kind,
                  subject: subject.trim(),
                  address: address.trim() || undefined,
                  description: description.trim() || undefined,
                });
                toast.success('gov.requests.sent');
                closeAsk();
              } catch (err) {
                toast.error(err, 'gov.admin.errors.actionFailed');
              }
            })}
          >
            {t('gov.requests.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} fullWidth maxWidth="sm">
        {open && (
          <>
            <DialogTitle>{open.subject}</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <StatusChip
                    status={statusTone.serviceRequest[open.status]}
                    label={t(`gov.requests.statuses.${open.status}`)}
                  />
                  <TagChip label={t(`gov.requests.kinds.${open.kind}`)} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t(received(open) ? 'gov.requests.receivedFrom' : 'gov.requests.sentTo', {
                    name: counterpartName(open),
                  })}
                  {open.createdByName ? ` · ${open.createdByName}` : ''} ·{' '}
                  {formatDate(open.createdAt, i18n.language)}
                </Typography>
                {open.address && <Typography variant="body2">{open.address}</Typography>}
                {open.description && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {open.description}
                  </Typography>
                )}
                {open.settlementSummary && (
                  <Alert severity={open.status === 'settled' ? 'success' : 'warning'}>
                    {open.settlementSummary}
                  </Alert>
                )}
                {received(open) &&
                  can('request:handle') &&
                  SERVICE_REQUEST_NEXT[open.status].length > 0 &&
                  !open.linkedFileId && (
                    <TextField
                      select
                      label={t('gov.requests.linkFile')}
                      value={linkFileId}
                      onChange={(e) => setLinkFileId(e.target.value)}
                      helperText={t(
                        open.kind === 'file_access'
                          ? 'gov.requests.linkFileUnlockHelp'
                          : 'gov.requests.linkFileHelp',
                      )}
                      fullWidth
                    >
                      <MenuItem value="">{t('gov.requests.noFile')}</MenuItem>
                      {linkable.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.reference} — {f.address}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                {received(open) &&
                  can('request:handle') &&
                  SERVICE_REQUEST_NEXT[open.status].length > 0 && (
                    <TextField
                      label={t('gov.requests.answer')}
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      helperText={t('gov.requests.answerHelp')}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(null)}>{t('common.actions.close')}</Button>
              {received(open) &&
                can('request:handle') &&
                SERVICE_REQUEST_NEXT[open.status].map((to) => (
                  <Button
                    key={to}
                    variant={to === 'settled' ? 'contained' : 'text'}
                    color={to === 'declined' ? 'error' : 'primary'}
                    disabled={
                      move.isPending ||
                      ((to === 'settled' || to === 'declined') && answer.trim().length < 3) ||
                      // Settling a request to see a file is granting access to it,
                      // so the file has to be named.
                      (to === 'settled' &&
                        open.kind === 'file_access' &&
                        open.direction === 'lg_to_company' &&
                        !open.linkedFileId &&
                        !linkFileId)
                    }
                    onClick={() => void moveTo(to)}
                  >
                    {t(`gov.requests.to.${to}`)}
                  </Button>
                ))}
            </DialogActions>
          </>
        )}
      </Dialog>
    </AppShell>
  );
}
